import { useGetCurrentUserQuery } from '../../../redux/apis';
import { useGetPlatformPendingProductEarningsQuery } from '../../../redux/apis/endpoints/paymentApi';
import { toast } from 'react-hot-toast';
import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectUserCountry } from '../../../redux/slices/locationSlice';

// Import extracted components and utilities
import BalanceCard from '../../../components/wallet/BalanceCard';
import TransactionHistory from '../../../components/wallet/TransactionHistory';
import MerchantVerification from '../../../components/wallet/MerchantVerification';
import MerchantFundCollectionPayments from '../../../components/wallet/MerchantFundCollectionPayments';
import CountryConfirmDialog from '../../../components/wallet/CountryConfirmDialog';
import { getCountryName, getCurrencyFromCountryCode } from '../../../utils/countryUtils';
import { 
  getAvailableBalance, 
  getPendingBalance, 
  getInstantAvailableBalance
} from '../../../components/wallet/balanceUtils';

// Import the new hook
import { useStripeConnect } from '../../../hooks/useStripeConnect';
const userLang = navigator.language || navigator.userLanguage;

const ProfileWallet = () => {
  const location = useLocation();
  const { data: user, refetch: refetchUserData, isLoading: isUserLoading } = useGetCurrentUserQuery();
  const userCountryFromSlice = useSelector(selectUserCountry);
  const [showRedirectMessage, setShowRedirectMessage] = useState(false);
  const [userDisplayCurrency, setUserDisplayCurrency] = useState('USD');

  const {
    accountStatus,
    isStatusError,
    statusError,
    accountBalanceData,
    isBalanceLoading,
    isBalanceError,
    balanceError,
    isVerifying,
    verificationStep,
    handleStripeVerification,
    getVerificationButtonText,
    showCountryDialog,
    setShowCountryDialog,
    detectedCountry,
    isLoadingOverall: isLoadingStripeConnect
  } = useStripeConnect(user, refetchUserData, userCountryFromSlice);

  const {
    data: platformPendingEarningsData,
    isLoading: isPlatformPendingEarningsLoading,
    error: platformPendingEarningsError,
  } = useGetPlatformPendingProductEarningsQuery(undefined, {
    skip: !user,
  });
  
  useEffect(() => {
    if (location.state?.message) {
      setShowRedirectMessage(true);
    }
  }, [location]);

  useEffect(() => {
    if (user?.country) {
      setUserDisplayCurrency(getCurrencyFromCountryCode(user.country));
    } else if (userCountryFromSlice?.code) {
      setUserDisplayCurrency(getCurrencyFromCountryCode(userCountryFromSlice.code));
          } else {
      setUserDisplayCurrency('USD');
    }
  }, [user, userCountryFromSlice?.code]);

  const handleCountryConfirm = (country) => {
    if (window.confirmStripeCountry) {
      window.confirmStripeCountry(country);
    }
    setShowCountryDialog(false);
  };

  const handleCountryCancel = () => {
    if (window.cancelStripeCountrySelection) {
      window.cancelStripeCountrySelection(new Error('Country selection cancelled by user.'));
    }
    setShowCountryDialog(false);
  };

  const pagePrimaryLoading = isUserLoading || isPlatformPendingEarningsLoading;

  return (
    <div className="p-6 space-y-6">
      <CountryConfirmDialog 
        open={showCountryDialog}
        setOpen={setShowCountryDialog}
        detectedCountry={detectedCountry}
        countryName={getCountryName(detectedCountry)}
        onConfirm={handleCountryConfirm}
        onCancel={handleCountryCancel}
      />

      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">
          {userLang === 'zh-CN' ? '钱包' : 'Wallet'}
        </h2>
      </div>

      {showRedirectMessage && !accountStatus?.status?.chargesEnabled && (
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-700">
                {userLang === 'zh-CN' 
                  ? '您需要完成Stripe验证才能上传产品。请完成以下验证步骤。' 
                  : 'You need to complete Stripe verification before uploading products. Please complete the verification process below.'}
              </p>
            </div>
          </div>
        </div>
      )}

      {platformPendingEarningsError && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
          <p className="text-sm text-red-700">
            {userLang.startsWith('zh') 
              ? '加载平台待结算金额失败。' 
              : 'Failed to load platform pending earnings.'}
          </p>
        </div>
      )}

      {pagePrimaryLoading && !isLoadingStripeConnect ? (
        <div className="flex justify-center py-12">
          <svg className="animate-spin h-8 w-8 text-brand-teal" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
      ) : (
        <>
          <BalanceCard 
            availableBalance={getAvailableBalance(accountBalanceData, user)}
            pendingBalance={getPendingBalance(accountBalanceData, user)}
            instantAvailableBalance={getInstantAvailableBalance(accountBalanceData)}
            platformPendingProductEarningsData={platformPendingEarningsData}
            lastUpdated={accountBalanceData ? new Date().toLocaleTimeString() : null}
            displayCurrency={userDisplayCurrency}
          />

          <MerchantVerification
            accountStatus={accountStatus}
            statusError={statusError}
            isStatusError={isStatusError}
            isVerifying={isVerifying}
            verificationStep={verificationStep}
            onVerify={handleStripeVerification}
            getVerificationButtonText={getVerificationButtonText}
            isLoading={isLoadingStripeConnect}
          />

          <MerchantFundCollectionPayments />

          <TransactionHistory 
            transactions={accountBalanceData?.transactions || []}
            isLoading={isBalanceLoading}
            error={balanceError}
          />
        </>
      )}
    </div>
  );
};

export default ProfileWallet; 